import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Compass, Rocket, Users, Sparkles, Landmark, Layers, Database, Search, Map, Handshake, LineChart, Activity, GraduationCap,
  Award, Eye, Scale, Globe, Target,
} from "lucide-react";
import Seo from "@/components/common/Seo";
import { Reveal, KineticHeading, KineticHeadingInView } from "@/components/common/Reveal";
import CTASection from "@/components/common/CTASection";
import StrategyNetwork from "@/components/heroes/AdvisoryCompass";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";

const SERVICES = [
  { icon: Compass, title: "Technology Strategy", body: "A clear, board-ready direction that ties every technology decision to a business objective." },
  { icon: Map, title: "Business Technology Roadmap", body: "A prioritised, sequenced plan that turns strategy into decisions the business can execute." },
  { icon: Users, title: "Fractional CIO Leadership", body: "Senior, board-level technology leadership on the cadence your business requires." },
  { icon: Sparkles, title: "AI Strategy", body: "A focused, governed view of where AI creates genuine business value, and where it does not." },
  { icon: Rocket, title: "Digital Transformation", body: "Senior ownership of change, from the business case through to a measurable result." },
  { icon: Landmark, title: "Technology Governance", body: "The oversight and decision rights that keep major technology choices accountable to the board." },
  { icon: Database, title: "ERP Strategy", body: "Independent leadership on the largest, riskiest core platform decisions the business will make." },
  { icon: Search, title: "Technology Due Diligence", body: "A clear, independent read on technology value and risk ahead of a major decision or transaction." },
  { icon: LineChart, title: "Technology Investment Review", body: "An honest assessment of where the money is going and what the business is actually receiving in return." },
  { icon: Handshake, title: "Vendor Evaluation", body: "Objective evaluation of partners, platforms and providers, free of commercial bias." },
  { icon: Activity, title: "Business Continuity", body: "Confidence that the operating core keeps running as the business grows and conditions change." },
  { icon: GraduationCap, title: "Executive Coaching", body: "A trusted sounding board for leaders carrying significant technology decisions." },
];

const CHALLENGES = [
  { title: "Technology decisions without executive guidance", body: "Consequential calls being made without a senior, independent voice in the room." },
  { title: "Business growth creating operational complexity", body: "Ambition is moving faster than the operating model and systems built to support it." },
  { title: "ERP no longer supporting business objectives", body: "The core platform is capping speed, cost and the ability to move on new opportunities." },
  { title: "Unclear AI priorities", body: "Pressure to act on AI without a clear, commercial view of where it actually pays." },
  { title: "Increasing cybersecurity risks", body: "Exposure sits on the board's agenda without a clear line of sight or ownership." },
  { title: "Vendor-driven technology decisions", body: "Critical choices are being shaped by suppliers rather than by the business itself." },
  { title: "Technology costs increasing without measurable business value", body: "Spend keeps rising while the link to revenue, margin or growth stays unclear." },
  { title: "Difficulty aligning technology with business strategy", body: "The technology agenda and the business plan are pulling in different directions." },
  { title: "Expansion into new markets requiring stronger governance", body: "New geographies and sites are exposing gaps in oversight, control and continuity." },
  { title: "Lack of long-term technology planning", body: "Decisions are made in cycles that are too short to protect the business over the next decade." },
];

const OUTCOMES = [
  { title: "Business Growth", note: "New revenue, markets and capacity." },
  { title: "Operational Excellence", note: "A leaner, more resilient core." },
  { title: "Investment Optimisation", note: "Spend tied to business return." },
  { title: "Executive Visibility", note: "A clear board-level line of sight." },
  { title: "Business Agility", note: "The ability to move with speed." },
  { title: "Risk Reduction", note: "Cyber and delivery risk controlled." },
  { title: "Improved Governance", note: "Decisions held to account." },
  { title: "Scalable Operations", note: "Systems built for the next stage." },
  { title: "Stronger Leadership Decisions", note: "Clarity to decide with confidence." },
  { title: "Competitive Advantage", note: "Capability rivals cannot copy." },
];

const ENGAGEMENT = [
  { title: "Understand business goals", body: "Start with the strategy, the economics and the outcomes leadership is accountable for." },
  { title: "Assess current technology landscape", body: "A candid read on current capability, cost and business risk against those goals." },
  { title: "Identify business priorities", body: "Focus on the two or three decisions that will most shape the next stage of the business." },
  { title: "Develop executive roadmap", body: "A prioritised, board-ready plan that turns strategy into decisions the business can execute." },
  { title: "Support leadership decisions", body: "Provide senior, independent counsel on the calls that carry the most business consequence." },
  { title: "Guide execution", body: "Hold delivery, teams and vendors accountable to the business outcome, not the activity." },
  { title: "Review business outcomes", body: "Measure results against the value that was promised, in the board's own terms." },
];

const WHY = [
  { icon: Target, title: "Business-first thinking", body: "Every recommendation begins with the business result it is meant to create. Technology follows the strategy, never the other way around." },
  { icon: Award, title: "27+ years leadership experience", body: "Judgement built in real boardrooms, on real programmes, with real capital at stake, not theory." },
  { icon: Scale, title: "Independent executive advice", body: "No products to sell and no platforms to push. The only interest is the decision that is right for your business." },
  { icon: Handshake, title: "Vendor-neutral recommendations", body: "Objective evaluation of partners, platforms and providers, free of commercial bias or hidden incentives." },
  { icon: Eye, title: "Executive-level communication", body: "Technology translated into the terms on which executives actually decide, no jargon, no theatre." },
  { icon: Layers, title: "Flexible engagement model", body: "A cadence that flexes with the business, from periodic board input to a more intensive presence through a critical programme." },
  { icon: Compass, title: "Long-term trusted partnership", body: "A senior advisor who stays through delivery and beyond, accountable to the outcome, not just the advice." },
  { icon: Globe, title: "Experience across India, the UAE and the Gulf", body: "Direct experience of business conditions across the region, and how technology decisions play out in practice." },
];

const INDUSTRIES = [
  "Growing Businesses", "Family-Owned Businesses", "Manufacturing", "Engineering", "Industrial Products",
  "Retail", "Healthcare", "Logistics", "Infrastructure", "Companies Expanding Regionally",
];

const FAQS = [
  { q: "How is Executive Advisory different from consulting?", a: "A consulting firm typically deploys a team to deliver a project and hand over a report. Executive advisory is a senior, ongoing relationship, a single, accountable voice who sits alongside your leadership on the decisions that matter and stays with the outcome. The measure is not deliverables, it is better decisions and a stronger business." },
  { q: "How often do advisory engagements take place?", a: "The cadence is shaped around your business, from a regular monthly executive session to a more intensive presence during a major decision or programme. The commitment flexes up and down as priorities shift." },
  { q: "Can Executive Advisory work alongside our internal IT team?", a: "Yes. The role is to lead and elevate your existing team, not replace it. That means setting direction, raising the standard of delivery, and giving your people a senior leader to work alongside on the decisions that carry the most business consequence." },
  { q: "Can Executive Advisory support AI strategy?", a: "Yes. The priority is to separate genuine, commercial AI value from noise, build a focused portfolio the business can adopt with confidence, and put the governance in place to scale responsibly. AI is a business question first and a technology question second." },
  { q: "Can Executive Advisory support ERP modernisation?", a: "Yes. ERP and core platform decisions are among the largest and riskiest investments a business will ever make. Executive advisory brings independent judgement to the decision, the partner and the delivery, protecting both the investment and the value it is meant to create." },
  { q: "Can Executive Advisory support expansion into the UAE and Gulf markets?", a: "Yes. Expansion into the UAE and the wider Gulf places real weight on governance, operating model and technology capacity. Executive advisory helps leadership teams plan the capabilities required to scale cleanly, drawing on direct experience of business conditions across India, the UAE and the Gulf Region." },
];

const serviceLd = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Executive Technology Advisory",
  serviceType: "Executive Technology Advisory Services",
  description: "Independent, board-level advisory for growing businesses, family-owned enterprises and executive leadership teams across India, the UAE and the Gulf Region. Technology strategy, digital transformation, AI, governance and due diligence.",
  provider: { "@type": "Person", name: "Aftab Azad" },
  areaServed: ["India", "United Arab Emirates", "Gulf Region", "Middle East", "Singapore", "Global"],
  url: "https://aftabazad.com/services/executive-advisory",
};

function scrollToServices() {
  const el = document.getElementById("advisory-services");
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
  else el.scrollIntoView({ behavior: "smooth" });
}

export default function ExecutiveAdvisory() {
  return (
    <>
      <Seo
        title="Executive Technology Advisory Services"
        description="Executive advisory that helps growing businesses make better technology decisions. Aftab Azad works with business owners, CEOs and executive teams across India, the UAE and the Gulf Region."
        path="/services/executive-advisory"
        keywords={["Executive Technology Advisor", "Executive Advisory Services", "Board Technology Advisor", "Technology Strategy Consultant", "Digital Transformation Advisor", "AI Strategy Advisor", "Family-Owned Business Advisor", "Mid-Market Technology Leadership"]}
        jsonLd={serviceLd}
      />

      {/* SECTION 1 — HERO */}
      <section data-testid="advisory-hero" className="relative overflow-hidden bg-navy pb-24 pt-40 text-white grain-overlay md:pb-28 md:pt-48">
        <div className="pointer-events-none absolute -right-40 top-0 h-[600px] w-[600px] rounded-full bg-royal/20 blur-[140px]" />
        <StrategyNetwork className="pointer-events-none absolute inset-y-10 right-0 hidden h-[calc(100%-5rem)] w-[38%] max-w-[520px] pr-4 opacity-90 md:block lg:w-[40%]" />
        <div className="relative mx-auto max-w-[1400px] px-6 md:px-12">
          <p className="font-body text-xs font-bold uppercase tracking-[0.3em] text-sky">Executive Advisory</p>
          <h1 className="mt-6 max-w-5xl font-heading text-4xl font-light leading-[1.04] tracking-tight md:text-[3.4rem]">
            <KineticHeading lines={["Executive advisory for", "growing businesses,"]} lineClassName="font-light" />
            <KineticHeading lines={["business decisions first."]} lineClassName="font-black text-sky" />
          </h1>
          <p className="mt-8 max-w-2xl font-body text-lg leading-relaxed text-slate-300 md:text-xl">
            Technology influences business growth, operational performance, customer experience and
            long-term competitiveness. Growing businesses often need experienced executive guidance
            to make confident technology decisions without adding another full-time executive to the
            leadership team. Aftab Azad works closely with business owners, CEOs, managing directors
            and executive teams to ensure technology investments support measurable business outcomes
            across India, the UAE and the Gulf Region.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link to="/book" data-testid="advisory-hero-book"
              className="group inline-flex items-center gap-2 rounded-full bg-royal px-8 py-4 font-body text-base font-semibold text-white transition-colors duration-300 hover:bg-sky hover:text-navy">
              Book Executive Strategy Session
              <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
            <Link to="/services/fractional-cio" data-testid="advisory-hero-explore"
              className="inline-flex items-center gap-2 rounded-full border border-white/20 px-8 py-4 font-body text-base font-medium text-white transition-colors hover:border-sky hover:text-sky">
              Explore Fractional CIO Services
            </Link>
          </div>
          <div className="mt-12 flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-white/10 pt-6">
            {["27+ years executive leadership", "India", "UAE", "Gulf Region", "Global"].map((t, i, arr) => (
              <span key={t} className="flex items-center gap-3">
                <span className="font-body text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">{t}</span>
                {i < arr.length - 1 && <span className="h-1 w-1 rounded-full bg-sky/50" />}
              </span>
            ))}
          </div>
          <button type="button" onClick={scrollToServices} data-testid="advisory-hero-anchor" className="sr-only">Explore advisory areas</button>
        </div>
      </section>

      {/* SECTION 2 — CORE ADVISORY SERVICES */}
      <section id="advisory-services" data-testid="advisory-services" className="bg-surface py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="max-w-2xl">
            <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">Executive advisory areas</p>
            <h2 className="mt-6 font-heading text-4xl font-light leading-[1.02] tracking-tight text-navy md:text-5xl">
              <KineticHeadingInView lines={["The full range of", "the boardroom's", "technology questions."]} />
            </h2>
          </div>
          <div className="mt-10 md:mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal key={s.title} delay={(i % 3) * 0.05}>
                  <div className="flex h-full flex-col rounded-[1.5rem] border border-slate-200 bg-white p-8">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-navy/5 text-navy">
                      <Icon className="h-6 w-6" strokeWidth={1.5} />
                    </div>
                    <h3 className="mt-6 font-heading text-xl font-bold leading-snug tracking-tight text-navy">{s.title}</h3>
                    <p className="mt-3 font-body leading-relaxed text-slate-600">{s.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 3 — BUSINESS CHALLENGES */}
      <section data-testid="advisory-challenges" className="bg-white py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="max-w-2xl">
            <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">Business challenges we help solve</p>
            <h2 className="mt-6 font-heading text-4xl font-light leading-[1.02] tracking-tight text-navy md:text-5xl">
              <KineticHeadingInView lines={["Where growing", "businesses get stuck."]} />
            </h2>
          </div>
          <div className="mt-10 md:mt-12 grid gap-x-12 gap-y-10 md:grid-cols-2">
            {CHALLENGES.map((c) => (
              <Reveal key={c.title} className="border-t border-slate-200 pt-6">
                <h3 className="font-heading text-xl font-bold tracking-tight text-navy">{c.title}</h3>
                <p className="mt-2 max-w-md font-body leading-relaxed text-slate-600">{c.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4 — BUSINESS OUTCOMES */}
      <section data-testid="advisory-outcomes" className="relative overflow-hidden bg-navy py-24 text-white md:py-32 grain-overlay">
        <div className="pointer-events-none absolute -left-32 top-1/2 h-[400px] w-[400px] -translate-y-1/2 rounded-full bg-royal/20 blur-[120px]" />
        <div className="relative mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="max-w-2xl">
            <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-sky">Business outcomes</p>
            <h2 className="mt-6 font-heading text-4xl font-light leading-[1.02] tracking-tight md:text-5xl">
              <KineticHeadingInView lines={["The value the", "board can see."]} />
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-2 gap-px overflow-hidden rounded-[1.5rem] border border-white/10 bg-white/10 lg:grid-cols-5">
            {OUTCOMES.map((o) => (
              <div key={o.title} className="bg-navy/80 px-6 py-8 backdrop-blur">
                <div className="font-heading text-base font-semibold leading-snug tracking-tight text-white">{o.title}</div>
                <p className="mt-2 font-body text-sm leading-relaxed text-slate-400">{o.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 5 — ENGAGEMENT MODEL */}
      <section data-testid="advisory-engagement" className="bg-white py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="max-w-2xl">
            <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">Our advisory approach</p>
            <h2 className="mt-6 font-heading text-4xl font-light leading-[1.02] tracking-tight text-navy md:text-5xl">
              <KineticHeadingInView lines={["A clear path,", "from objective", "to outcome."]} />
            </h2>
          </div>
          <div className="mt-16 max-w-3xl">
            {ENGAGEMENT.map((s, i) => (
              <Reveal key={s.title} className="relative flex gap-6 pb-10 last:pb-0">
                {i < ENGAGEMENT.length - 1 && <span aria-hidden className="absolute left-6 top-14 h-[calc(100%-2.5rem)] w-px bg-slate-200" />}
                <div className="relative z-10 flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-navy font-heading text-lg font-bold text-white">{i + 1}</div>
                <div className="pt-1.5">
                  <h3 className="font-heading text-xl font-bold tracking-tight text-navy">{s.title}</h3>
                  <p className="mt-2 font-body leading-relaxed text-slate-600">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 6 — WHY AFTAB */}
      <section data-testid="advisory-why" className="bg-surface py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="max-w-2xl">
            <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">Why business leaders work with Aftab Azad</p>
            <h2 className="mt-6 font-heading text-4xl font-light leading-[1.02] tracking-tight text-navy md:text-5xl">
              <KineticHeadingInView lines={["The difference", "in the room."]} />
            </h2>
          </div>
          <div className="mt-10 md:mt-12 grid gap-x-12 gap-y-14 md:grid-cols-2">
            {WHY.map((w) => {
              const Icon = w.icon;
              return (
                <Reveal key={w.title} className="flex gap-6 border-t border-slate-200 pt-8">
                  <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-navy/5 text-navy">
                    <Icon className="h-6 w-6" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-heading text-2xl font-bold tracking-tight text-navy">{w.title}</h3>
                    <p className="mt-3 max-w-md font-body text-lg leading-relaxed text-slate-600">{w.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 7 — INDUSTRIES */}
      <section data-testid="advisory-industries" className="bg-white py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">Who we work with</p>
              <h2 className="mt-6 font-heading text-4xl font-light leading-[1.05] tracking-tight text-navy md:text-5xl">
                <KineticHeadingInView lines={["Growing businesses,", "family-owned groups,", "leadership teams."]} />
              </h2>
              <p className="mt-8 max-w-md font-body text-lg leading-relaxed text-slate-600">
                Aftab primarily advises growing businesses, family-owned enterprises and executive
                leadership teams preparing for the next stage of growth. Experience spans manufacturing,
                engineering, industrial products, retail, healthcare, logistics and infrastructure,
                with clients across India, the UAE and the Gulf Region.
              </p>
            </div>
            <div className="grid gap-px overflow-hidden rounded-[1.5rem] border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:col-span-7">
              {INDUSTRIES.map((name) => (
                <div key={name} data-testid={`advisory-industry-${name.toLowerCase().replace(/\s|&/g, "-")}`} className="flex items-center bg-white p-6 transition-colors hover:bg-surface">
                  <span className="h-2 w-2 flex-shrink-0 rounded-full bg-royal" />
                  <h3 className="ml-4 font-heading text-lg font-bold text-navy">{name}</h3>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 8 — FAQ */}
      <section data-testid="advisory-faq" className="bg-surface py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="max-w-2xl">
            <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">Frequently asked questions</p>
            <h2 className="mt-6 font-heading text-4xl font-light leading-[1.02] tracking-tight text-navy md:text-5xl">
              <KineticHeadingInView lines={["Straight answers,", "before we talk."]} />
            </h2>
          </div>
          <Accordion type="single" collapsible className="mt-14 max-w-4xl border-t border-slate-200">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid={`advisory-faq-${i}`} className="border-b border-slate-200">
                <AccordionTrigger className="py-6 font-heading text-xl font-bold text-navy hover:no-underline data-[state=open]:text-royal">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="max-w-3xl font-body text-lg leading-relaxed text-slate-600">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* SECTION 9 — CLOSING */}
      <section data-testid="advisory-closing" className="relative overflow-hidden bg-navy py-24 text-white md:py-32 grain-overlay">
        <div className="pointer-events-none absolute -right-32 top-1/2 h-[400px] w-[400px] -translate-y-1/2 rounded-full bg-royal/20 blur-[120px]" />
        <div className="relative mx-auto max-w-4xl px-6 text-center md:px-12">
          <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-sky">A closing thought</p>
          <blockquote className="mt-8 font-heading text-3xl font-light leading-[1.3] tracking-tight md:text-4xl">
            Technology decisions should strengthen business performance, not increase complexity. The
            role of an Executive Technology Advisor is to help leadership teams make practical, informed
            and confident decisions that support sustainable business growth.
          </blockquote>
        </div>
      </section>

      <CTASection
        overline="Let's build a technology strategy"
        heading={["That supports your", "business growth."]}
        body="Whether your organisation is preparing for expansion, modernising enterprise systems, evaluating AI opportunities or seeking experienced executive technology leadership, let's discuss how practical executive advisory can support your long-term business goals."
        primary={{ label: "Schedule Executive Strategy Session", to: "/book" }}
        secondary={{ label: "Contact Aftab", to: "/contact" }}
      />
    </>
  );
}
